import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, CheckCircle2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { SeoHead } from "@/components/SeoHead";

type Preview = {
  workspace_id: string;
  workspace_label: string | null;
  invited_email: string;
  role: string;
  status: string;
  expires_at: string;
  inviter_email: string | null;
};

const ROLE_LABEL: Record<string, string> = {
  owner_admin: "Owner / admin",
  editor: "Editor",
  viewer: "Viewer",
};

const InviteAccept = () => {
  const { token = "" } = useParams<{ token: string }>();
  const { user, loading: authLoading } = useAuth();
  const { setActive, refresh } = useWorkspace();
  const navigate = useNavigate();

  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Load invite preview (works for anonymous + authenticated visitors).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const { data, error } = await supabase.rpc(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "get_workspace_invite_preview" as any,
        { _token: token },
      );
      if (cancelled) return;
      if (error) {
        setPreviewError(error.message);
      } else {
        const row = Array.isArray(data) && data.length > 0 ? (data[0] as Preview) : null;
        if (!row) {
          setPreviewError("Invitation not found.");
        } else {
          setPreview(row);
        }
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async () => {
    if (!user || !preview) return;
    setAccepting(true);
    const { data, error } = await supabase.rpc(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      "accept_workspace_invite" as any,
      { _token: token },
    );
    setAccepting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const row = Array.isArray(data) && data.length > 0 ? (data[0] as { workspace_id: string }) : null;
    setAccepted(true);
    toast.success("You're in!");
    await refresh();
    if (row?.workspace_id) {
      setActive(row.workspace_id);
    }
    setTimeout(() => navigate("/excerpts"), 700);
  };

  // Build a sign-in/sign-up link that brings the user back here after auth.
  const returnTo = `/invite/${token}`;
  const authUrl = preview
    ? `/auth?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(preview.invited_email)}&returnTo=${encodeURIComponent(returnTo)}`
    : `/auth?returnTo=${encodeURIComponent(returnTo)}`;

  if (loading || authLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md shadow-soft">
          <CardContent className="space-y-3 py-8">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (previewError || !preview) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md shadow-soft border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Invitation problem
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>{previewError ?? "We couldn't load this invitation."}</p>
            <Button asChild variant="outline">
              <Link to="/">Go home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isExpired = new Date(preview.expires_at).getTime() < Date.now();
  const notPending = preview.status !== "pending";
  const blocked = isExpired || notPending;

  const blockedMessage = isExpired
    ? "This invitation has expired. Please ask the workspace owner to send a new one."
    : preview.status === "accepted"
      ? "This invitation has already been accepted."
      : preview.status === "revoked"
        ? "This invitation was revoked."
        : "This invitation is no longer valid.";

  const wsName = preview.workspace_label || "this workspace";

  // Email mismatch — only relevant once signed in.
  const emailMismatch =
    !!user &&
    !!user.email &&
    user.email.toLowerCase() !== preview.invited_email.toLowerCase();

  return (
    <>
    <SeoHead title="Accept invitation — Excerptus" description="Join an Excerptus workspace." noindex />
    <div className="min-h-dvh flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MailCheck className="h-5 w-5 text-primary" />
            You're invited
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm">
            <span className="text-muted-foreground">
              {preview.inviter_email ?? "A teammate"} invited you to join
            </span>{" "}
            <strong className="text-foreground">{wsName}</strong>{" "}
            <span className="text-muted-foreground">as</span>{" "}
            <strong className="text-foreground">
              {ROLE_LABEL[preview.role] ?? preview.role}
            </strong>
            .
          </p>
          <p className="text-xs text-muted-foreground">
            Sent to <span className="font-mono">{preview.invited_email}</span>
          </p>

          {blocked ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              {blockedMessage}
            </div>
          ) : accepted ? (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              Joined — taking you in…
            </div>
          ) : !user ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Sign in or create an account with{" "}
                <span className="font-mono">{preview.invited_email}</span> to accept.
              </p>
              <Button asChild className="w-full">
                <Link to={authUrl}>Sign in to accept</Link>
              </Button>
            </div>
          ) : emailMismatch ? (
            <div className="space-y-2">
              <div className="rounded-md border border-yellow-500/30 bg-yellow-500/5 p-3 text-sm">
                You're signed in as <strong>{user.email}</strong>, but this invite was sent to{" "}
                <strong>{preview.invited_email}</strong>. Sign out and back in with the invited address.
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate(authUrl);
                }}
              >
                Sign out
              </Button>
            </div>
          ) : (
            <Button onClick={accept} disabled={accepting} className="w-full">
              {accepting ? "Joining…" : "Accept invitation"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default InviteAccept;
